import {
  Menu,
  MenuItem,
  MenuTrigger,
} from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import * as styles from './styles.css';
import { OrganizeService } from '@affine/core/modules/organize';
import { FolderRole } from '@affine/core/modules/db/schema/schema';

const getRoleName = (t: ReturnType<typeof useI18n>, role?: FolderRole) => {
  switch (role) {
    case FolderRole.Admin:
      return t['com.affine.share-menu.option.permission.can-manage']();
    case FolderRole.Write:
      return t['com.affine.share-menu.option.permission.can-edit']();
    case FolderRole.Read:
      return t['com.affine.share-menu.option.permission.can-read']();
    case FolderRole.None:
      return t['com.affine.share-menu.option.permission.no-access']();
    default:
      return '';
  }
};

export const MembersPermission = ({
  nodeId,
}: {
  nodeId: string;
}) => {
  const t = useI18n();

  const organizeService = useService(OrganizeService);

  const node = useLiveData(
    organizeService.folderTree.folderNode$(nodeId)
  );

  const defaultRole = useLiveData(
    node?.defaultRole$
  );

  const currentRoleName = useMemo(
    () => getRoleName(t, defaultRole),
    [defaultRole, t]
  );

  const changePermission = useAsyncCallback(
    async (docRole: FolderRole) => {
      console.info('changedefaultRole', docRole);
      node?.updateDefaultRole(docRole);
    },
    [node]
  );

  const selectManage = useCallback(() => {
    changePermission(FolderRole.Admin);
  }, [changePermission]);

  const selectEdit = useCallback(() => {
    changePermission(FolderRole.Write);
  }, [changePermission]);

  const selectRead = useCallback(() => {
    changePermission(FolderRole.Read);
  }, [changePermission]);

  const selectNone = useCallback(() => {
    changePermission(FolderRole.None);
  }, [changePermission]);

  return (
    <div className={styles.rowContainerStyle}>
      <div className={styles.labelStyle}>
        {t['com.affine.share-menu.option.permission.label']()}
      </div>

        <Menu
          contentOptions={{
            align: 'end',
          }}
          items={
            <>
              <MenuItem
                onSelect={selectManage}
                selected={defaultRole === FolderRole.Admin}
              >
                <div className={styles.publicItemRowStyle}>
                  {t['com.affine.share-menu.option.permission.can-manage']()}
                </div>
              </MenuItem>
              <MenuItem
                onSelect={selectEdit}
                selected={defaultRole === FolderRole.Write}
              >
                <div className={styles.publicItemRowStyle}>
                  <div className={styles.tagContainerStyle}>
                    {t['com.affine.share-menu.option.permission.can-edit']()}
                  </div>
                </div>
              </MenuItem>
              <MenuItem
                onSelect={selectRead}
                selected={defaultRole === FolderRole.Read}
              >
                <div className={styles.publicItemRowStyle}>
                  <div className={styles.tagContainerStyle}>
                    {t['com.affine.share-menu.option.permission.can-read']()}
                  </div>
                </div>
              </MenuItem>
              <MenuItem
                onSelect={selectNone}
                selected={defaultRole === FolderRole.None}
              >
                <div className={styles.publicItemRowStyle}>
                  <div className={styles.tagContainerStyle}>
                    {t['com.affine.share-menu.option.permission.no-access']()}
                  </div>
                </div>
              </MenuItem>
            </>
          }
        >
          <MenuTrigger
            className={styles.menuTriggerStyle1}
            variant="plain"
            suffixClassName={styles.suffixClassName}
            contentStyle={{
              width: '100%',
            }}
          >
            {currentRoleName}
          </MenuTrigger>
        </Menu>
    </div>
  );
};

